import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, ExternalLink, Calendar, Play, Sparkles, StopCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { trackEvent } from '@/lib/analytics';
import { supabase } from '@/integrations/supabase/client';

// Feature flag for AI mode - NOW ENABLED
const ENABLE_AI_CHAT = true;

// Brand colors as CSS variables - WCAG AA accessible
const brandColors = {
  primary: '#0891B2',     // Accessible cyan (4.5:1 contrast with white)
  lightCyan: '#A6F1FF',
  bodyText: '#44235F',
  headings: '#7130A0',
  lightPurple: '#DFD8E8',
  emphasis: '#C41067',    // Accessible fuchsia (4.5:1 contrast with white)
  gradient: 'linear-gradient(90deg, #7130A0 0%, #C41067 100%)',
};

interface QAPair {
  id: string;
  question: string;
  answer: string;
  keywords: string[];
}

interface Message {
  id: string;
  type: 'user' | 'bot' | 'suggestions';
  content: string;
  suggestions?: QAPair[];
  isAiResponse?: boolean;
  isStreaming?: boolean;
}

interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
}

// Approved Knowledge Base
const knowledgeBase: QAPair[] = [
  {
    id: 'trial-free',
    question: 'Is the trial free?',
    answer: 'Yes. The trial is obligation-free. No credit card is required. You only need your RTO ID to get started.',
    keywords: ['trial', 'free', 'cost', 'price', 'pay', 'obligation'],
  },
  {
    id: 'credit-card',
    question: 'Do I need a credit card?',
    answer: 'No credit card is required to start your free trial. Simply enter your RTO ID to get started. You can choose a plan later if you decide to continue after the trial.',
    keywords: ['credit', 'card', 'payment', 'pay', 'billing', 'charge'],
  },
  {
    id: 'rto-id',
    question: 'How does RTO ID work?',
    answer: 'Your RTO ID is your unique identifier as a Registered Training Organisation. When you sign up, ComplyHub uses your RTO ID to pre-populate your organisation details and set up your compliance registers. You can find your RTO ID on training.gov.au.',
    keywords: ['rto', 'id', 'identifier', 'number', 'registration', 'training.gov', 'signup', 'sign up'],
  },
  {
    id: 'trial-start',
    question: 'Where do I start a free trial?',
    answer: 'Start your free trial at our trial signup page. You will need your RTO ID.',
    keywords: ['start', 'begin', 'trial', 'signup', 'sign up', 'register'],
  },
  {
    id: 'after-trial',
    question: 'What happens after the trial?',
    answer: 'You can choose a plan to continue. There is no automatic charge from the marketing site.',
    keywords: ['after', 'trial', 'end', 'expire', 'finish', 'charge', 'automatic'],
  },
  {
    id: 'change-plans',
    question: 'Can I change plans later?',
    answer: 'Yes. Plans can be changed as your needs change.',
    keywords: ['change', 'plan', 'switch', 'upgrade', 'downgrade', 'later'],
  },
  {
    id: 'who-for',
    question: 'Who is ComplyHub for?',
    answer: 'ComplyHub is designed for RTO Compliance Managers, RTO Managers, and senior leadership who need consistent audit readiness and clearer governance.',
    keywords: ['who', 'for', 'target', 'user', 'audience', 'rto', 'manager', 'compliance'],
  },
  {
    id: 'what-does',
    question: 'What does ComplyHub do?',
    answer: 'It centralises compliance registers, evidence, and governance workflows so you can manage compliance work and produce audit-ready outputs.',
    keywords: ['what', 'does', 'do', 'feature', 'function', 'purpose', 'help'],
  },
  {
    id: 'guarantee',
    question: 'Does ComplyHub guarantee compliance?',
    answer: 'No. ComplyHub supports structured compliance and audit readiness, but responsibility for compliance remains with the RTO.',
    keywords: ['guarantee', 'compliance', 'ensure', 'promise', 'certain'],
  },
  {
    id: 'auditors-see',
    question: 'What do auditors see?',
    answer: 'Structured registers and evidence, version-controlled documents, and exportable records. See our auditors page for examples.',
    keywords: ['auditor', 'see', 'view', 'audit', 'evidence', 'register', 'document'],
  },
  {
    id: 'user-access',
    question: 'Can we control user access?',
    answer: 'Yes. Role-based access controls support different user needs across administrators, compliance, and trainers.',
    keywords: ['access', 'control', 'role', 'permission', 'user', 'admin', 'trainer'],
  },
  {
    id: 'version-control',
    question: 'Do you have version control for documents?',
    answer: 'Yes. Documents can be managed with versions, review cycles, and status tracking.',
    keywords: ['version', 'control', 'document', 'history', 'track', 'review'],
  },
  {
    id: 'setup-time',
    question: 'How long does setup take?',
    answer: 'Most RTOs can start quickly. Setup time depends on how much existing data you load.',
    keywords: ['setup', 'time', 'long', 'start', 'implement', 'onboard', 'quick'],
  },
  {
    id: 'book-demo',
    question: 'Can I book a demo?',
    answer: 'Yes. Use our demo page to choose a time that works for you.',
    keywords: ['book', 'demo', 'demonstration', 'show', 'meeting', 'call'],
  },
];

const quickQuestions = [
  'Is the trial free?',
  'Do I need a credit card?',
  'What does ComplyHub do?',
  'What plans are available?',
  'What do auditors see?',
  'How does RTO ID work?',
];

// Streaming AI-powered response function
async function streamAiAnswer(
  question: string, 
  conversationHistory: ConversationMessage[],
  onDelta: (text: string) => void,
  onDone: () => void,
  onError: (error: string) => void,
  signal?: AbortSignal
): Promise<void> {
  const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-assistant`;
  
  try {
    trackEvent('chat_ai_request', { question_length: question.length });
    
    const resp = await fetch(CHAT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({ 
        message: question,
        conversationHistory 
      }),
      signal,
    });

    // Handle error responses
    if (!resp.ok) {
      if (resp.status === 429) {
        trackEvent('chat_ai_error', { error: 'rate_limited' });
        onError("I'm receiving too many requests right now. Please try again in a moment.");
        return;
      }
      if (resp.status === 402) {
        trackEvent('chat_ai_error', { error: 'payment_required' });
        onError("The AI service is temporarily unavailable. Please try selecting from the suggested questions instead.");
        return;
      }
      
      // Try to parse JSON error
      try {
        const errorData = await resp.json();
        if (errorData?.error) {
          trackEvent('chat_ai_error', { error: errorData.code || 'unknown' });
          onError(errorData.error);
          return;
        }
      } catch {
        // Not JSON, continue with generic error
      }
      
      throw new Error(`HTTP ${resp.status}`);
    }

    if (!resp.body) {
      throw new Error('No response body');
    }

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let textBuffer = '';
    let streamDone = false;
    let fullResponse = '';

    while (!streamDone) {
      const { done, value } = await reader.read();
      if (done) break;
      textBuffer += decoder.decode(value, { stream: true });

      // Process line-by-line as data arrives
      let newlineIndex: number;
      while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
        let line = textBuffer.slice(0, newlineIndex);
        textBuffer = textBuffer.slice(newlineIndex + 1);

        if (line.endsWith('\r')) line = line.slice(0, -1);
        if (line.startsWith(':') || line.trim() === '') continue;
        if (!line.startsWith('data: ')) continue;

        const jsonStr = line.slice(6).trim();
        if (jsonStr === '[DONE]') {
          streamDone = true;
          break;
        }

        try {
          const parsed = JSON.parse(jsonStr);
          const content = parsed.choices?.[0]?.delta?.content as string | undefined;
          if (content) {
            fullResponse += content;
            onDelta(content);
          }
        } catch {
          // Incomplete JSON split across chunks: put it back and wait for more data
          textBuffer = line + '\n' + textBuffer;
          break;
        }
      }
    }

    // Final flush in case remaining buffered lines arrived without trailing newline
    if (textBuffer.trim()) {
      for (let raw of textBuffer.split('\n')) {
        if (!raw) continue;
        if (raw.endsWith('\r')) raw = raw.slice(0, -1);
        if (raw.startsWith(':') || raw.trim() === '') continue;
        if (!raw.startsWith('data: ')) continue;
        const jsonStr = raw.slice(6).trim();
        if (jsonStr === '[DONE]') continue;
        try {
          const parsed = JSON.parse(jsonStr);
          const content = parsed.choices?.[0]?.delta?.content as string | undefined;
          if (content) {
            fullResponse += content;
            onDelta(content);
          }
        } catch { /* ignore partial leftovers */ }
      }
    }

    trackEvent('chat_ai_response', { response_length: fullResponse.length });
    onDone();
  } catch (err) {
    // Handle abort specifically
    if (err instanceof Error && err.name === 'AbortError') {
      console.log('AI chat aborted by user');
      trackEvent('chat_ai_stopped', {});
      onDone(); // Call onDone to finalize what we have
      return;
    }
    console.error('AI chat exception:', err);
    trackEvent('chat_ai_error', { error: 'exception' });
    onError("I'm having trouble connecting. Please try one of the suggested questions below.");
  }
}

function findMatchingQuestions(input: string): QAPair[] {
  const normalizedInput = input.toLowerCase().trim();
  const words = normalizedInput.split(/\s+/);
  
  const scored = knowledgeBase.map(qa => {
    let score = 0;
    words.forEach(word => {
      if (word.length < 2) return;
      qa.keywords.forEach(keyword => {
        if (keyword.includes(word) || word.includes(keyword)) {
          score += 1;
        }
      });
      if (qa.question.toLowerCase().includes(word)) {
        score += 0.5;
      }
    });
    return { qa, score };
  });
  
  return scored
    .filter(item => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map(item => item.qa);
}

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversationHistory, setConversationHistory] = useState<ConversationMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const navigate = useNavigate();

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus trap and escape key handling
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
      
      // Focus trap
      if (e.key === 'Tab' && panelRef.current) {
        const focusableElements = panelRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
        
        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    inputRef.current?.focus();
    
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const handleOpen = () => {
    setIsOpen(true);
    trackEvent('chat_open', {});
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleDemoClick = () => {
    trackEvent('chat_cta_click', { cta: 'demo' });
    navigate('/demo');
    setIsOpen(false);
  };

  const handleTrialClick = () => {
    trackEvent('chat_cta_click', { cta: 'trial' });
    navigate('/trial-signup');
    setIsOpen(false);
  };

  const handleStopGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsStreaming(false);
      // Mark any streaming messages as no longer streaming
      setMessages(prev => 
        prev.map(msg => 
          msg.isStreaming ? { ...msg, isStreaming: false } : msg
        )
      );
    }
  };

  const addBotMessage = (content: string, isAiResponse: boolean = false) => {
    const id = Date.now().toString();
    setMessages(prev => [...prev, { id, type: 'bot', content, isAiResponse }]);
  };

  const addSuggestionsMessage = (suggestions: QAPair[]) => {
    const id = Date.now().toString();
    setMessages(prev => [...prev, { 
      id, 
      type: 'suggestions', 
      content: 'Here are some questions I can help with:',
      suggestions 
    }]);
  };

  const handleQuestionSelect = (qa: QAPair) => {
    trackEvent('chat_question_selected', { question_id: qa.id });
    
    // Add user message
    const userId = Date.now().toString();
    setMessages(prev => [...prev, { id: userId, type: 'user', content: qa.question }]);
    
    // Simulate typing
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      addBotMessage(qa.answer);
    }, 500);
  };

  const handleQuickQuestion = (question: string) => {
    if (question === 'Book a demo') {
      handleDemoClick();
      return;
    }
    
    const match = knowledgeBase.find(qa => qa.question === question);
    if (match) {
      handleQuestionSelect(match);
    } else {
      // Handle "What plans are available?"
      const userId = Date.now().toString();
      setMessages(prev => [...prev, { id: userId, type: 'user', content: question }]);
      setIsTyping(true);
      setTimeout(() => {
        setIsTyping(false);
        addBotMessage('For detailed pricing information, please visit our pricing page. Plans can be adjusted as your needs change.');
      }, 500);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    
    const userInput = inputValue.trim();
    setInputValue('');
    
    // Add user message
    const userId = Date.now().toString();
    setMessages(prev => [...prev, { id: userId, type: 'user', content: userInput }]);
    
    // Add to conversation history
    const updatedHistory: ConversationMessage[] = [
      ...conversationHistory,
      { role: 'user', content: userInput }
    ];
    setConversationHistory(updatedHistory);
    
    setIsTyping(true);
    
    // Check if AI mode is enabled
    if (ENABLE_AI_CHAT) {
      // Create a streaming message ID
      const streamingMsgId = `streaming-${Date.now()}`;
      let streamedContent = '';
      let hasError = false;
      
      // Create abort controller for this request
      abortControllerRef.current = new AbortController();
      setIsStreaming(true);
      
      // Add initial empty AI message with streaming flag
      setMessages(prev => [...prev, { 
        id: streamingMsgId, 
        type: 'bot', 
        content: '', 
        isAiResponse: true,
        isStreaming: true
      }]);
      setIsTyping(false);
      
      await streamAiAnswer(
        userInput,
        conversationHistory,
        // onDelta - update message content
        (delta) => {
          streamedContent += delta;
          setMessages(prev => 
            prev.map(msg => 
              msg.id === streamingMsgId 
                ? { ...msg, content: streamedContent }
                : msg
            )
          );
        },
        // onDone - finalize and remove streaming flag
        () => {
          setMessages(prev => 
            prev.map(msg => 
              msg.id === streamingMsgId 
                ? { ...msg, isStreaming: false }
                : msg
            )
          );
          if (streamedContent) {
            setConversationHistory(prev => [...prev, { role: 'assistant', content: streamedContent }]);
          }
          setIsStreaming(false);
          abortControllerRef.current = null;
        },
        // onError - show error and suggestions
        (errorMsg) => {
          hasError = true;
          setMessages(prev => 
            prev.map(msg => 
              msg.id === streamingMsgId 
                ? { ...msg, content: errorMsg, isAiResponse: false, isStreaming: false }
                : msg
            )
          );
          const matches = findMatchingQuestions(userInput);
          if (matches.length > 0) {
            addSuggestionsMessage(matches);
          } else {
            addSuggestionsMessage(knowledgeBase.slice(0, 6));
          }
          setIsStreaming(false);
          abortControllerRef.current = null;
        },
        abortControllerRef.current.signal
      );
      return;
    }
    
    // FAQ mode: find matching questions
    const matches = findMatchingQuestions(userInput);
    
    setTimeout(() => {
      setIsTyping(false);
      
      if (matches.length > 0) {
        addSuggestionsMessage(matches);
      } else {
        trackEvent('chat_no_match', { query: userInput });
        addBotMessage("I can help with common questions. Try one of these:");
        addSuggestionsMessage(knowledgeBase.slice(0, 6));
      }
    }, 500);
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          className="fixed bottom-20 right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-full shadow-lg transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 animate-bounce-subtle"
          style={{ 
            backgroundColor: brandColors.primary,
            color: 'white',
          }}
          aria-label="Open chat with our team"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="font-medium">Chat</span>
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-end justify-end p-4 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="chat-title"
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/20 backdrop-blur-sm"
            onClick={handleClose}
          />
          
          {/* Panel */}
          <div 
            ref={panelRef}
            className="relative w-full max-w-md h-[600px] max-h-[80vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
            style={{ border: `2px solid ${brandColors.lightPurple}` }}
          >
            {/* Header */}
            <div 
              className="flex items-center justify-between px-5 py-4"
              style={{ background: brandColors.gradient }}
            >
              <div>
                <h2 
                  id="chat-title" 
                  className="text-lg font-semibold text-white"
                >
                  ComplyHub Assistant
                </h2>
                <p className="text-sm text-white/80">
                  Product and trial questions
                </p>
              </div>
              <button
                onClick={handleClose}
                className="p-2 rounded-full hover:bg-white/20 transition-colors text-white"
                aria-label="Close chat panel"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Quick Actions */}
            <div 
              className="flex gap-2 px-4 py-3 border-b"
              style={{ 
                backgroundColor: brandColors.lightCyan,
                borderColor: brandColors.lightPurple 
              }}
            >
              <button
                onClick={handleDemoClick}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105"
                style={{ 
                  backgroundColor: brandColors.primary,
                  color: 'white' 
                }}
              >
                <Calendar className="w-4 h-4" />
                Book a demo
              </button>
              <button
                onClick={handleTrialClick}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105"
                style={{ 
                  backgroundColor: brandColors.emphasis,
                  color: 'white' 
                }}
              >
                <Play className="w-4 h-4" />
                Start free trial
              </button>
            </div>

            {/* Messages */}
            <div 
              className="flex-1 overflow-y-auto px-4 py-4 space-y-4"
              style={{ backgroundColor: 'white' }}
            >
              {messages.length === 0 ? (
                <div className="space-y-4">
                  <p 
                    className="text-sm"
                    style={{ color: brandColors.bodyText }}
                  >
                    Hi! I can answer common questions about ComplyHub. Select a topic or type your question.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {quickQuestions.map((question, index) => (
                      <button
                        key={index}
                        onClick={() => handleQuickQuestion(question)}
                        className="px-3 py-2 text-sm rounded-full transition-all hover:scale-105"
                        style={{ 
                          backgroundColor: brandColors.lightPurple,
                          color: brandColors.headings,
                          border: `1px solid ${brandColors.headings}20`
                        }}
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((message) => (
                  <div key={message.id}>
                    {message.type === 'user' && (
                      <div className="flex justify-end">
                        <div 
                          className="max-w-[80%] px-4 py-2 rounded-2xl rounded-br-sm text-white"
                          style={{ background: brandColors.gradient }}
                        >
                          {message.content}
                        </div>
                      </div>
                    )}
                    {message.type === 'bot' && (
                      <div className="flex justify-start">
                        <div 
                          className="max-w-[80%] px-4 py-2 rounded-2xl rounded-bl-sm"
                          style={{ 
                            backgroundColor: brandColors.lightCyan,
                            color: brandColors.bodyText
                          }}
                        >
                          {message.isAiResponse && (
                            <div className="flex items-center gap-1 mb-1 text-xs" style={{ color: brandColors.emphasis }}>
                              <Sparkles className="w-3 h-3" />
                              <span>AI Response</span>
                            </div>
                          )}
                          {message.isAiResponse ? (
                            <div className="prose prose-sm max-w-none [&>p]:mb-2 [&>p:last-child]:mb-0 [&>ul]:my-2 [&>ol]:my-2 [&>li]:my-0.5 [&_a]:underline [&_a]:font-medium [&_strong]:font-semibold [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs" style={{ color: brandColors.bodyText }}>
                              <ReactMarkdown
                                components={{
                                  a: ({ href, children }) => (
                                    <a
                                      href={href}
                                      target={href?.startsWith('http') ? '_blank' : undefined}
                                      rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                                      style={{ color: brandColors.primary }}
                                      onClick={(e) => {
                                        if (href && !href.startsWith('http')) {
                                          e.preventDefault();
                                          navigate(href);
                                        }
                                      }}
                                    >
                                      {children}
                                    </a>
                                  ),
                                  code: ({ children }) => (
                                    <code style={{ backgroundColor: brandColors.lightPurple }}>
                                      {children}
                                    </code>
                                  ),
                                }}
                              >
                                {message.content}
                              </ReactMarkdown>
                              {message.isStreaming && (
                                <span 
                                  className="inline-block w-0.5 h-4 ml-0.5 align-middle animate-pulse"
                                  style={{ backgroundColor: brandColors.emphasis }}
                                  aria-hidden="true"
                                />
                              )}
                            </div>
                          ) : (
                            <>
                              <span>{message.content}</span>
                              {message.isStreaming && (
                                <span 
                                  className="inline-block w-0.5 h-4 ml-0.5 align-middle animate-pulse"
                                  style={{ backgroundColor: brandColors.emphasis }}
                                  aria-hidden="true"
                                />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                    {message.type === 'suggestions' && (
                      <div className="space-y-2">
                        <p 
                          className="text-sm"
                          style={{ color: brandColors.bodyText }}
                        >
                          {message.content}
                        </p>
                        <div className="flex flex-col gap-2">
                          {message.suggestions?.map((qa) => (
                            <button
                              key={qa.id}
                              onClick={() => handleQuestionSelect(qa)}
                              className="text-left px-3 py-2 text-sm rounded-lg transition-all hover:scale-[1.02]"
                              style={{ 
                                backgroundColor: brandColors.lightPurple,
                                color: brandColors.headings,
                                border: `1px solid ${brandColors.headings}30`
                              }}
                            >
                              {qa.question}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
              
              {isTyping && (
                <div className="flex justify-start">
                  <div 
                    className="px-4 py-2 rounded-2xl rounded-bl-sm"
                    style={{ backgroundColor: brandColors.lightCyan }}
                  >
                    <div className="flex gap-1">
                      <span 
                        className="w-2 h-2 rounded-full animate-bounce"
                        style={{ backgroundColor: brandColors.headings, animationDelay: '0ms' }}
                      />
                      <span 
                        className="w-2 h-2 rounded-full animate-bounce"
                        style={{ backgroundColor: brandColors.headings, animationDelay: '150ms' }}
                      />
                      <span 
                        className="w-2 h-2 rounded-full animate-bounce"
                        style={{ backgroundColor: brandColors.headings, animationDelay: '300ms' }}
                      />
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form 
              onSubmit={handleSubmit}
              className="flex items-center gap-2 px-4 py-3 border-t"
              style={{ 
                backgroundColor: 'white',
                borderColor: brandColors.lightPurple 
              }}
            >
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your question..."
                className="flex-1 px-4 py-2 rounded-full text-sm focus:outline-none focus:ring-2"
                style={{ 
                  backgroundColor: brandColors.lightPurple,
                  color: brandColors.bodyText,
                  borderColor: brandColors.headings,
                }}
              />
              {isStreaming ? (
                <button
                  type="button"
                  onClick={handleStopGeneration}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105"
                  style={{ 
                    backgroundColor: brandColors.emphasis,
                    color: 'white'
                  }}
                  aria-label="Stop generating response"
                >
                  <StopCircle className="w-4 h-4" />
                  Stop
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!inputValue.trim()}
                  className="p-2 rounded-full transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ 
                    backgroundColor: brandColors.primary,
                    color: 'white'
                  }}
                  aria-label="Send your message"
                >
                  <Send className="w-5 h-5" />
                </button>
              )}
            </form>

            {/* Footer */}
            <div 
              className="px-4 py-2 text-center text-xs"
              style={{ 
                backgroundColor: brandColors.lightPurple,
                color: brandColors.bodyText 
              }}
            >
              <span>Quick links: </span>
              <button 
                onClick={() => { navigate('/pricing'); setIsOpen(false); }}
                className="underline hover:no-underline"
                style={{ color: brandColors.headings }}
              >
                Pricing
              </button>
              <span> · </span>
              <button 
                onClick={() => { navigate('/auditors'); setIsOpen(false); }}
                className="underline hover:no-underline"
                style={{ color: brandColors.headings }}
              >
                Audit walkthrough
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
